/* global document, window */

const url = require('url');
const debug = require('debug')('plugin:Client');
const isPlainObject = require('lodash/isPlainObject');
const { parallel, trimSlash, addLeadingSlash } = require('./util');
const MessageBus = require('./MessageBus');
const dom = require('./dom');
const Router = require('./Router');
const util = require('./util');
const localStorage = require('public/js/lib/local-storage.js');

const ROUTING = require('./const/routing');
const ACTIONS = require('./const/actions');
const REQUEST_METHODS = require('./const/request-methods');

const placeholderHtml = require('html-loader!../static/plugin-fatal-failure.html');

const VALID_ORIGINS = [
	'https://revolutionparts.com',
	'https://revolutionparts.vpc',
];

// Specific HTML consts
const PARTS_CATEGORY_SEARCH_ID = 'parts-category-search';

const ROUTING_ATTRIBUTE_TYPE = {
	ignore: 'ignore',
};

// Once reached, we show an error to the user
// This is the time from JS + iframe load to not having a message event from the iframe
// In my testing, this didn't occur with a timeout of 0. This is padding to give the render a chance.
const IFRAME_LOAD_TIMEOUT = 2000;

const IFRAME_HEIGHT_PADDING = 20;

const TIMERS = {
	IFRAME_LOAD: 'iframe_load',
};

module.exports = class PluginClient {
	constructor({window, renderEl, iframe, env, jwta}) {
		debug('constructor', env);
		this.isInitialLoad = true;
		this.renderEl = renderEl;
		this.window = window;
		this.document = window.document;
		this.pluginHash = env.pluginHash;
		this.originalTitle = window.document.title;
		this.serverUrl = env.serverUrl;
		this.isClickHandlerAttached = false;
		this.timers = {};
		this.isBusConnected = false;
		this.urlJwta = jwta;

		const validRoutingTypes = Object.keys(ROUTING.types);

		if (validRoutingTypes.indexOf(env.routingMethod) === -1) {
			env.routingMethod = ROUTING.types.query;
		}

		if (env.routingMethod === ROUTING.types.path) {
			this.routingMethod = ROUTING.types.path;
		} else {
			this.routingMethod = ROUTING.types.query;
		}

		/*if (window.$ || window.jquery) {
			this.parentJquery = window.jQuery.noConflict();
		}*/

		this.addEventListener = (el, ...args) => {
			if (window.addEventListener) {
				return el.addEventListener(...args);
			} else {
				args[0] = `on${args[0]}`;
				return el.attachEvent(...args);
			}
		};

		// TODO - Response handler needs to be split out of this file...
		this.responseHandlers = {};
		this.responseHandlerIncrement = 0;

		window.__PLUGIN_SCRIPTS = {};
		window.__IS_WEBSTORE_PLUGIN = true;

		this.setupGlobals({window});

		this.initRouter({
			window,
			onPopStateHook: this.onPopState.bind(this),
			routingMethod: this.routingMethod,
		});

		this.initMessageBus({
			source: window,
			validOrigins: VALID_ORIGINS
		});

		this.initIframeHandler({ iframe });
	}

	initIframeHandler({iframe}) {
		iframe.onload = this.handleIframeLoad.bind(this);
	}

	handleIframeLoad() {
		if (!this.isBusConnected) {
			this.timers[TIMERS.IFRAME_LOAD] = setTimeout(() => {
				this.renderEl.innerHTML = placeholderHtml;
			}, IFRAME_LOAD_TIMEOUT);
		}
	}

	initMessageBus({source, target, validOrigins}) {
		debug('initMessageBus', source, target, validOrigins);
		this.bus = new MessageBus({
			validOrigins,
			source,
			target,
		});

		this.bus.addMessageHandler(this.handleMessage.bind(this));
	}

	initRouter(opts={}) {
		this.router = new Router(opts);
	}

	setClientBase({baseUrl, basePath}) {
		this.clientBaseUrl = baseUrl;
		this.clientBasePath = basePath;
		this.clientUrl = url.parse(`${baseUrl}${basePath}`);
	}

	/**
	 * @description On history (Router) pop event you get the Router.go Promise
	 * @param {Promise} routing
	 */
	onPopState(routing) {
		debug('onPopState');
		this.showLoading();
		routing
			.then(this.onRoute())
			.catch(this.onRouteFailure());
	}

	showLoading() {
		debug('showLoading');
		if (this.$) {
			this.overlaySpinner = this.$('#rp_webstore').overlaySpinner({fixedPosition: true});
		}
	}

	hideLoading() {
		debug('hideLoading');
		if (this.$ && this.overlaySpinner) {
			this.overlaySpinner.overlaySpinner.remove();
		}
	}

	onRoute() {
		return ({url}) => {
			debug('onRoute', url);
			this.showLoading();
			this.abortAllRequests();
			// We need to make requests with the path minus the client path
			this.makeRequest({url: this.router.getWebstoreUrlFromClientUrl(url)});

			if (this.$) {
				this.$('.modal.in').modal('hide');
				this.$('.modal-backdrop').remove();
			}
		};
	}

	onRouteFailure() {
		return (...args) => {
			debug('onRouteFailure', ...args);
			this.hideLoading();
		};
	}

	handleMessage({data={}, origin, source}) {
		try {
			// Cannot log origin/source here because it triggers DOM security exceptions
			debug('handleMessage', data);
		} catch(e) {
			debug('handleMessage:exception', e);
		}

		switch (data.type) {
			case ACTIONS.error:
				this.updateClientDom(data.body || {});
				break;
			case ACTIONS.ready:
				this.onReadyMessageInit({data, origin, source});
				this.router.go(this.window.location)
					.then(this.onRoute())
					.catch(this.onRouteFailure());
				break;
			case ACTIONS.clientUpdate:
				this.updateClientDom(data.body || {});
				break;
			case ACTIONS.clientJsonUpdate:
				this.fulfillJsonResponse(data);
				break;
			case ACTIONS.updatePath:
				this.updatePath(data);
				break;
			case ACTIONS.updateLoadingSpinner:
				this.updateLoadingSpinner(data);
				break;
			case ACTIONS.updateIframeContainerHeight:
				this.updateIframeContainerHeight(data);
				break;
			case ACTIONS.unloadAccount:
				this.displayAccountLogoutWarning();
				break;
			case ACTIONS.redirect:
				this.handleRedirect(data.url);
				break;
		}
	}

	updatePath(data) {
		if (!isPlainObject(data)) { return; }
		const preventNavigation = (typeof data.preventNavigation === 'undefined') ? true : data.preventNavigation;
		// Only do something if we have a path to change to
		if (data.path) {
			// Get the current page path
			const currentPath = addLeadingSlash(window.location.pathname);
			const pieces = currentPath.split('?path=');  // Divide into 2 pieces, the actual site path, and everything else
			const basePath = pieces[0];

			// Build and encode the desired path
			const desiredPath = data.usePluginIframe ? `/secure?framepath=${data.path}` : data.path;
			const encodedPath = encodeURIComponent(desiredPath);

			// Put it all together and de-lish
			const fullPath = `${basePath}?path=${encodedPath}`;
			this.router.go(fullPath, { preventNavigation })
				.then(this.onRoute())
				.catch(this.onRouteFailure());
		}
	}

	updateLoadingSpinner(data) {
		if (!isPlainObject(data)) { return; }
		if (data.display) {
			this.showLoading();
		} else {
			this.hideLoading();
		}
	}

	updateIframeContainerHeight(data) {
		if (!isPlainObject(data)) { return; }
		if (data.height) {
			const iframe = document.querySelector('iframe.plugin-embed');
			if (iframe) {
				const height = (Number(data.height) + IFRAME_HEIGHT_PADDING);  // extra pixel padding just for good measure
				iframe.style.height = `${height}px`;
			}
		}
	}

	// NOTE: Because of how browsers handle the 'beforeunload' event, this will only happen AFTER the user cancels the unload (which then triggers the action)
	displayAccountLogoutWarning() {
		const displayLength = 5000;
		const message = document.querySelector('#unload_message');
		if (message) {
			message.style.display = 'block';
		} else {
			const style = `
				background-color: #fff;
				text-align: center;
				padding: .6em .5em;
				font-size: 1.6em;
			`;
			const text = `For your security, you will be logged out upon exiting the page.`;
			const el = document.createElement('div');
			el.innerHTML = `<div id="unload_message" style="${style}">${text}<div>`;
			const plugin = document.querySelector('#rp_plugin');
			plugin.insertBefore(el, plugin.firstChild);
		}

		window.setTimeout(function () {
			const message = document.querySelector('#unload_message');
			message.style.display = 'none';
		}, displayLength);
	}

	onReadyMessageInit({data, origin, source}) {
		const urlObj = url.parse(data.baseUrl);
		const {host, pathname:basePath} = urlObj;
		const currentHost = window.location.hostname;

		const cleanLocationHost = currentHost.replace(/^(?:www\.)?/i, '');
		const cleanHost = host.replace(/^(?:www\.)?/i, '');

		if (cleanLocationHost !== cleanHost) {
			util.log('error', 'Current location and base host do not match');
		}

		// If JWT is returned (it should be), set it
		if (data.jwt) {
			this.setJWT(data.jwt);
		}

		const baseUrl = `${this.window.location.protocol}//${currentHost}`;

		if (this.timers[TIMERS.IFRAME_LOAD]) {
			clearTimeout(this.timers[TIMERS.IFRAME_LOAD]);
			this.timers[TIMERS.IFRAME_LOAD] = false;
		}

		this.isBusConnected = true;

		this.bus.target = source;
		this.setClientBase({baseUrl, basePath});
		// Re-setup globals to use the above client base
		this.setupGlobals({window:this.window});
		this.router.init({
			baseUrl,
			basePath,
		});

		// If we get a JWTA from the url, register it
		if (this.urlJwta) {
			this.sendMessage({
				type: ACTIONS.registerAccountJwt,
				body: {
					ajwt: this.urlJwta,
				},
			});
		}
	}

	fulfillJsonResponse(data={}) {
		debug('fulfillJsonResponse', data);
		if (data.requestId) {
			const responseHandler = this.getDataResponseHandler(data.requestId);
			responseHandler(data.body);
			this.removeDataResponseHandler(data.requestId);
		}
	}

	abortAllRequests() {
		this.sendMessage({
			type: ACTIONS.abort,
		});
	}

	handleRedirect(url) {
		this.window.location.replace(`${this.serverUrl}${url}`);
	}

	makeRequest({url, method=REQUEST_METHODS.get, body={}, type}) {
		debug('makeRequest', url, method, body, type);
		const jwt = this.getJWT();

		if (!url) return;
		if (url.indexOf('/') !== 0) {
			url = `/${url}`;
		}
		url = url.replace('/?search_str=', '/search?search_str=');

		const parsedUrl = util.parseUrl(url);
		if (parsedUrl && parsedUrl.pathname === '/cart') {
			this.sendMessage({
				jwt: jwt,
				type: ACTIONS.startRedirect,
				body: {
					url: parsedUrl.pathname,
				}
			});
			// Eventually we can do something like this once we have a in-client JS cart & add the ability (endpoint) to create a cart/checkout via the iframe
			// window.location.replace(`${this.serverUrl}/ajax/checkout/start`);
			return;
		}

		this.sendMessage({
			jwt: jwt,
			type: type || ACTIONS.request,
			body: {
				method: method || REQUEST_METHODS.get,
				url,
				data: body,
			},
		});
	}

	updateClientDom(data) {
		debug('updateClientDom', data);
		const {html='', scripts=[], styles=[], title='', rp_jwt=null} = data;

		if (rp_jwt) {
			this.setJWT(rp_jwt);
		}

		this.window.__PLUGIN_READY_QUEUE = [];

		// Load styles and scripts before page render
		parallel({
			styles: (callback) => {
				// This does our CSS reset via its own <style> tag
				// all: initial -- this does NOT work on IE, but resets all styles to initial Values
				dom.upsertStyle({
					// Style reset + overrides that can't be set in webstore
					// Rules to fight against parent page styles creeping into ours
					content: `
						#rp_plugin {
							all: initial;
							position: relative;
						}
						#rp_plugin * {
							z-index: initial;
							background-image: initial;
							max-width: initial;
							min-width: initial;
						}
						#rp_plugin img {
							display: initial;
						}
						#rp_plugin .modal .modal-dialog {
							margin-top: 7%;
							top: initial;
						}
						.modal-open #rp_plugin .modal {
							overflow: hidden auto;
						}
						body.modal-open {
							overflow: auto;
						}
						body.modal {
							overflow-y: auto;
						}
						#rp_webstore .modal {
							visibility: unset !important;
							background: rgba(0,0,0,0) !important;
						}
						#rp_webstore ul li {
							list-style: none !important;
						}
						#rp_webstore .modal ul {
							display: block !important;
						}
						#rp_webstore .card {
							padding: unset !important;
						}
						#rp_webstore .plugin-modal {
							z-index: 1041 !important;
						}
						#rp_webstore .plugin-modal-backdrop {
							z-index: 1040 !important;
						}
					`,
					id: 'client-style-reset'
				});

				styles.forEach(({content, id}) => {
					dom.upsertStyle({content, id});
				});
				callback(null, styles);
			},
			scripts: (callback) => {
				// Each "script" item has a src (file url), id (set in webstore with addScript), and Server.js (inside the iframe) adds the text content of the script to this object
				scripts.forEach(({src, id, content}) => {
					debug('updateClientDom -> execute script', {src, id});
					let tempScript;
					// We keep references to the internal, "exported" script function we catch with __CURRENT_SCRIPT__
					if (!this.window.__PLUGIN_SCRIPTS[src]) {
						debug('updateClientDom -> script cache miss', src);
						// Piggy-back on how Webpack injects values with the catch that we return the internal (non-webpack wrapped) function from each file
						tempScript = new Function(`
							return (function (__CURRENT_SCRIPT__) {
								${content}
								return __CURRENT_SCRIPT__;
							})();
						`);
						// Unwrap a function layer
						this.window.__PLUGIN_SCRIPTS[src] = tempScript();
					}
					// If we have a function for a given script id, execute
					if (typeof this.window.__PLUGIN_SCRIPTS[src] === 'function') {
						this.window.__PLUGIN_SCRIPTS[src]();
					}
					// Do what we can to force this into garbage collection
					tempScript = null;
				});
				callback(null, scripts);
			},
		}, (err, results) => {
			debug('updateClientDom -> scripts/styles loaded', err, results);
			// Update title (need to hold original title and change what we append on page load)
			if (title) {
				this.window.document.title = `${this.originalTitle} | ${title}`;
			} else {
				this.window.document.title = this.originalTitle;
			}

			// Grab jquery reference (otherwise we might not have all the .fn. plugins)
			if (this.window.rp$ && this.window.rp$.noConflict) {
				this.$ = this.window.rp$.noConflict();
			}

			if (this.router.isPushStateEnabled() && !this.isClickHandlerAttached && this.$) {
				const self = this;
				// Global click handler for anchors. Lightens the load on the page and simplifies the code/execution path
				this.$('#rp_webstore').on('click', 'a', function (e) {
					const url = self.$(this).attr('href');
					if (url.indexOf('javascript:') === -1) {
						e.preventDefault();
						if (self.$(this).data('routing') !== ROUTING_ATTRIBUTE_TYPE.ignore) {
							self.router.go(url)
								.then(self.onRoute())
								.catch(self.onRouteFailure());
						}
					}
				});
				this.isClickHandlerAttached = true;
			}

			// Then do a "render" i.e. content swap
			dom.replaceContent(this.renderEl, html, (err) => {
				debug('updateClientDom -> replaceContent');
				const anchors = this.renderEl.getElementsByTagName('a');
				const forms = this.renderEl.getElementsByTagName('form');
				const fieldsets = this.renderEl.getElementsByTagName('fieldset');
				const scripts = this.renderEl.getElementsByTagName('script');

				// Because we are in a different page and path, we need to rewrite all the things
				this.rewriteAnchors(anchors);
				if (forms.length > 0) {
					this.rewriteForms(forms);
				} else if (fieldsets.length > 0) {
					this.rewriteFieldsets(fieldsets);
				}
				this.rewriteSelect2SidebarDropdown();
				this.injectInlineScripts({
					targetEl: this.renderEl,
					scripts,
				});
				// Our client-side "on ready"
				this.clientReady();
				this.window.__PLUGIN_READY_QUEUE.forEach((item) => {
					item();
				});
				debug('updateClientDom -> done', err);
			});
		});
	}

	clientReady() {
		debug('clientReady');
		setTimeout(() => {
			debug('clientReady:execute');
			// TODO - This is a nice to have, but we need to make it smarter as some sites have fixed headers and this doesn't account for that.
			// if (this.$ && !this.isInitialLoad) {
			// 	const {top} = this.$('#rp_webstore').offset();
			// 	this.$('html, body').animate({ scrollTop: top }, 'fast');
			// }
			this.hideLoading();
			this.isInitialLoad = false;
		}, 0);
	}

	// Things like GA
	injectInlineScripts({targetEl, scripts}) {
		const len = scripts.length;

		for (let i = 0; i < len; i++) {
			const script = scripts[i];
			if (script && script.type === 'text/javascript') {
				const scriptText = script.textContent || script.text;

				if (targetEl && scriptText) {
					dom.insertScript({
						parentEl: targetEl,
						scriptBody: scriptText,
					});
				}
			}
		}

		// Then cleanup old stuff
		// Leaving this comment because it'd be nice to clean up old scrip tags but it doesn't hurt to leave them. They're imported "as text" and not executed until the above inject code happens. This creates duplicates in the markup, but it should cause no harm or cause extra execution.
		// for (let i = 0; i < len; i++) {
		// 	const script = scripts[i];
		// 	if (script) {
		// 		const parent = script.parentNode || script.parentElement;

		// 		if (parent) {
		// 			parent.removeChild(script);
		// 		}
		// 	}
		// }
	}

	rewriteAnchors(anchors) {
		const locationUrlObj = url.parse(this.window.location.href);

		for (let index = 0; index < anchors.length; index++) {
			const anchor = anchors[index] || {};
			if (!anchor || !anchor.href) {
				break;
			}

			let urlObj = url.parse(anchor.href);
			let {pathname} = urlObj;

			if (pathname && pathname.match(/undefined$/)) {
				urlObj.pathname = pathname.replace(/undefined$/, '');
				urlObj = url.parse(urlObj.format(urlObj));
			}

			let {pathname:path, href, hash} = urlObj;

			if (hash && this.shouldBeHashUrl(urlObj, locationUrlObj)) {
				// Handle hash-only hrefs
				anchor.href = url.format({path, pathname:path, hash});
				anchor.setAttribute('data-routing', ROUTING_ATTRIBUTE_TYPE.ignore);
			} else if (href && !this.router.shouldNotRoute(href)) {
				// Handle other hrefs
				anchor.href = this.rewriteUrl(urlObj);
				// BETA - We don't want to index plugin until we're ready
				anchor.rel = 'nofollow';
			}
		}
	}

	rewriteForms(forms) {
		for (let index = 0; index < forms.length; index++) {
			const form = forms[index] || {};
			const action = form.action;

			if (action) {
				form.action = this.rewriteUrl(action);
				if (this.router.isPushStateEnabled()) {
					this.hookForm(form);
				}
			}
		}
	}

	rewriteFieldsets(fieldsets) {
		for (let index = 0; index < fieldsets.length; index++) {
			const fieldset = fieldsets[index] || {};
			const action = fieldset.getAttribute('data-action');

			if (action) {
				const url = this.rewriteUrl(action);
				fieldset.setAttribute('data-action', url);
				if (this.router.isPushStateEnabled()) {
					this.hookFieldset(fieldset);
				}
			}
		}
	}

	rewriteSelect2SidebarDropdown() {
		const categorySearch = this.document.getElementById(PARTS_CATEGORY_SEARCH_ID);
		if (categorySearch) {
			const options = categorySearch.getElementsByTagName('option');
			for (let index = 0; index < options.length; index++) {
				const option = options[index] || {};
				const value = option.value;

				if (value) {
					option.value = this.rewriteUrl(value);
				}
			}
		}
	}

	rewriteUrl(url, opts={}) {
		return util.rewriteClientUrl(url, {
			routingMethod: this.routingMethod,
			targetHost: this.clientUrl.host,
			targetProtocol: this.clientUrl.protocol,
			targetBasePath: this.clientUrl.pathname,
			queryParams: opts.queryParams || undefined,
		});
	}

	// It should be a plain hash url (no path, host, etc.) if...
	// The host and path = base url
	// OR The host and path = current url
	shouldBeHashUrl(urlObj, locationUrlObj) {
		return (
			// Must have a hash
			urlObj.hash
			&& (
				// If url has no path, it's a basic hash
				(urlObj.pathname === '/')
				// If current window host and path match, it's a basic hash
				|| (urlObj.host === locationUrlObj.host
					&& trimSlash(urlObj.pathname) === trimSlash(locationUrlObj.pathname))
				// If client base host and path match, it's a basic hash
				|| (urlObj.host === this.clientUrl.host
					&& trimSlash(urlObj.pathname) === trimSlash(this.clientUrl.pathname))
			)
		);
	}

	hookAnchor(anchor) {
		this.addEventListener(anchor, 'click', (e) => {
			e.preventDefault();
			debug('hookAnchor:click', {href: anchor.href, anchor});
			this.router.go(anchor.href)
				.then(this.onRoute())
				.catch(this.onRouteFailure());
		}, true);
	}

	hookForm(form) {
		debug('hookForm', form);
		// Where we can use history API to do smooth (not full-page) reloads for users
		this.addEventListener(form, 'submit', (e) => {
			e.preventDefault();
			this.submitRequest(form, form.action, form.method);
		});
	}

	hookFieldset(fieldset) {
		debug('hookFieldset', fieldset);
		const action = fieldset.getAttribute('data-action') || false;
		const method = fieldset.getAttribute('data-method') || false;
		if (!action) {
			debug('Action not defined');
			return;
		}

		if (!method) {
			debug('Method not defined');
			return;
		}

		const buttons = fieldset.getElementsByTagName('button') || false;
		if (!buttons) {
			debug('Could not find fieldset buttons');
			return;
		}

		const buttonsArray = Array.from(buttons);
		const submitBtns = buttonsArray.filter(btn => btn.type === 'submit');
		if (!submitBtns) {
			debug('Could not find fieldset submit buttons');
			return;
		}

		const inputs = fieldset.getElementsByTagName('input') || false;
		if (!inputs) {
			debug('Could not find fieldset inputs');
			return;
		}

		const inputsArray = Array.from(inputs);

		submitBtns.forEach(submitBtn => {
			this.addEventListener(submitBtn, 'click', (e) => {
				e.preventDefault();
				this.submitRequest(fieldset, action, method);
			});
		});

		inputsArray.forEach(input => {
			this.addEventListener(input, 'keydown', (e) => {
				//On enter...
				if (e.keyCode === 13) {
					e.preventDefault();
					this.submitRequest(fieldset, action, method);
				}
			});
		});
	}

	submitRequest(form, action, method) {
		if (method && method.toUpperCase() === REQUEST_METHODS.get) {
			const getAction = url.parse(action || this.window.location.pathname);
			const queryParams = this.$(form).serializeArray();
			const routeUrl = this.rewriteUrl(getAction, {queryParams});
			debug('submitGetRequest:submit', {routeUrl});
			this.router.go(routeUrl)
				.then(this.onRoute())
				.catch(this.onRouteFailure());
		} else if (method && method.toUpperCase() === REQUEST_METHODS.post) {
			const postAction = this.router.getWebstoreUrlFromClientUrl(action);
			if (postAction) {
				const data = this.$(form).serializeArray();
				const body = {};
				data.forEach((item) => {
					body[item.name] = item.value;
				});
				debug('submitPostRequest:submit', {postAction, data});
				this.makeRequest({
					url: postAction,
					method: REQUEST_METHODS.post,
					body: {
						data: body, // OMG.. what have I done
					},
					type: ACTIONS.postRequest,
				});
			}
		}
		return false;
	}

	setupGlobals({window}) {
		window.__DISABLE_SEARCH_HISTORY_HANDLER = true;
		window.$ajax = this.getAjaxOverride();
		window.$location = this.getLocationOverride(window);

		// This might have stopped working all of the sudden
		// Either way, I discovered it was against spec to have more than one and really just a bad idea. Leaving it here to warn future peeps: don't add <base> to host page.
		// const baseEl = window.document.createElement('base');
		// baseEl.href = this.clientBaseUrl;
		// window.document.head.appendChild(baseEl);
	}

	getLocationOverride(window) {
		const location = require('public/js/lib/location')(window);
		location.redirect = (url) => {
			if (this.router.isPushStateEnabled()) {
				this.router.go(this.rewriteUrl(url))
					.then(this.onRoute())
					.catch(this.onRouteFailure());
			} else {
				window.location = this.rewriteUrl(url);
			}
		};
		location.getBase = () => this.clientBaseUrl;
		return location;
	}

	getAjaxOverride() {
		return util.$ajax({
			transport: (opts) => {
				debug('ajax transport', opts);

				let {
					method=REQUEST_METHODS.get,
					url,
					json,
					body,
					success,
					error,
				} = opts;

				const urlReplace = new RegExp(`^${this.clientBasePath}`, 'g');
				url = url.replace(urlReplace, '');

				const requestId = this.buildDataResponseHandlerRequestId([method, url, +new Date()]);

				this.addDataResponseHandler(requestId, (_data={}) => {
					const data = _data || {};
					debug('addDataResponseHandler -> invoke', requestId, data);

					if (data.rp_jwt) {
						this.setJWT(data.rp_jwt);
					}

					if (data.statusCode > 299) {
						error(data);
					} else {
						success(data, 'success');
					}
				});

				this.makeRequest({
					url: this.router.getWebstoreUrlFromClientUrl(url),
					method,
					body: {
						requestId: requestId,
						data: body,
					},
					type: ACTIONS.jsonRequest,
				});
			},
			transforms: [
				(opts={}) => {
					opts.url = this.rewriteUrl(opts.url);
					return opts;
				},
				(opts={}) => {
					if (opts.body && typeof opts.body === 'string') {
						// We need to send JS object/array and not a string of json
						if (opts.body.indexOf('{') === 0) {
							try {
								opts.body = JSON.parse(opts.body);
							} catch (e) {
								util.log('error', `Failed to parse XHR body pre-send ${opts.body}`);
								opts.body = {};
							}
						}
					}
					return opts;
				},
				(opts={}) => {
					const originalSuccess = opts.success;

					opts.success = (response, ...rest) => {
						// I didn't see an easy way to fix URLs for this endpoint... MONKEY PATCH
						if (opts.url && opts.url.indexOf('get-recommended-parts') !== -1) {
							if (response && Array.isArray(response)) {
								response = response.map((item) => {
									item.url = this.rewriteUrl(item.url);
									return item;
								});
							}
						}
						return originalSuccess(response, ...rest);
					};

					return opts;
				},
			],
		});
	}

	buildDataResponseHandlerRequestId(parts=[]) {
		this.responseHandlerIncrement += 1;
		return `${parts.join(':')}:${this.responseHandlerIncrement}`;
	}

	addDataResponseHandler(requestId, callback) {
		this.responseHandlers[requestId] = callback;
	}

	getDataResponseHandler(requestId) {
		return this.responseHandlers[requestId] || false;
	}

	removeDataResponseHandler(requestId) {
		if (this.responseHandlers[requestId]) {
			delete this.responseHandlers[requestId];
		}
	}

	buildMessage(message) {
		return message;
	}

	sendMessage(message) {
		this.bus.send({
			message: this.buildMessage(message),
			origin: '*', // TODO - ugh
		});
	}

	setJWT(token) {
		localStorage.setItem('RP_JWT', token);
	}

	getJWT() {
		return localStorage.getItem('RP_JWT');
	}
};
